<template>
  <div :class="formVariants({ theme })">
    <div class="mb-8 w-full xl:max-w-[335px]">
      <RadioGroup
        v-if="cmsPaymentTypes.length >= 1"
        v-model="paymentModel"
        name="payment"
        :options="cmsPaymentTypes"
        :theme="theme"
      />
      <Button
        v-if="siteOrg !== 'raddadjuren'"
        :title="paymentSystems[paymentModel].buttonTitle"
        color="pink"
        class="mt-8 w-full lg:mt-16"
        :arrow-right="paymentSystems[paymentModel].arrow"
        @click="$emit('onNext')"
      />
      <ButtonAct
        v-if="siteOrg === 'raddadjuren'"
        variant="primary"
        :title="paymentSystems[paymentModel].buttonTitle"
        :theme="theme"
        :isCta="true"
        :iconType="paymentSystems[paymentModel].arrow ? 'arrowRight' : undefined"
        @click="$emit('onNext')"
      />
    </div>

    <div
      :class="
        twMerge(
          'prose-a:text-blue prose-a:underline my-4 font-sans text-sm md:mb-8 lg:mb-4',
          theme === THEMES.RDK && 'text-base',
        )
      "
      v-if="text && typeof text === 'string'"
      v-html="text"
    ></div>
    <div v-else-if="text && !Array.isArray(text) && typeof text === 'object'">
      <ContentfulRichText
        :document="(text as any)"
        :class="
          twMerge(
            'prose-a:text-blue-600 prose-a:underline mt-4 font-sans text-sm',
            theme === THEMES.RDK && 'text-base',
          )
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { Button, RadioGroup, THEMES } from 'refresh-ui'
import { cva } from 'class-variance-authority'
import { useI18n } from 'vue-i18n'
import type { PaymentType } from '@/types/cms'
import type { SiteOrg } from '../../types/env'
import ButtonAct from '../atoms/ButtonAct.vue'
import { twMerge } from 'tailwind-merge'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    payment: PaymentSystemKeys
    options: PaymentType[]
    text?: string | object
    theme?: THEMES
    siteOrg?: SiteOrg
  }>(),
  {
    theme: THEMES.DEFAULT,
  },
)
const emits = defineEmits(['update:payment', 'onNext'])

const paymentModel = useVModel(props, 'payment', emits)

enum PAYMENTS {
  SWISH = 'swish',
  CARD = 'card',
  POSTAL = 'postal',
  INVOICE = 'invoice',
  FREE = 'free',
  F2F = 'f2f',
  STRIPE = 'stripe',
}
type PaymentProps = {
  label: string
  subTitle: string
  value: PAYMENTS
  buttonTitle: string
  arrow: boolean
}

type PaymentSystemKeys =
  | 'swish'
  | 'card'
  | 'postal'
  | 'free'
  | 'f2f'
  | 'stripe'
  | 'autogiro'
  | 'invoice'
// @ts-expect-error 'autogiro' is not in the paymentSystems object but is used in the PaymentSystemKeys type to make typescript happy
const paymentSystems: Record<PaymentSystemKeys, PaymentProps> = {
  swish: {
    label: t('type-swish'),
    subTitle: t('type-swish.description'),
    value: PAYMENTS.SWISH,
    buttonTitle: t('pay-by-swish'),
    arrow: true,
  },
  card: {
    label: t('type-card'),
    subTitle: t('type-card.description'),
    value: PAYMENTS.CARD,
    buttonTitle: t('pay-by-card'),
    arrow: true,
  },
  postal: {
    label: t('type-invoice'),
    subTitle: t('type-invoice.description'),
    value: PAYMENTS.POSTAL,
    buttonTitle: t('pay'), // pay-by-invoice // Note: Changed because the invoice label is too long and makes the button ugly
    arrow: true,
  },
  invoice: {
    label: t('type-invoice'),
    subTitle: t('type-invoice.description'),
    value: PAYMENTS.INVOICE,
    buttonTitle: t('pay-by-invoice'),
    arrow: true,
  },
  free: {
    label: t('type-free'),
    subTitle: t('type-free.description'),
    value: PAYMENTS.FREE,
    buttonTitle: t('next-step'),
    arrow: true,
  },
  f2f: {
    label: t('type-f2f'),
    subTitle: t('type-f2f.description'),
    value: PAYMENTS.F2F,
    buttonTitle: t('next-step'),
    arrow: true,
  },
  stripe: {
    label: t('type-stripe'),
    subTitle: t('type-stripe.description'),
    value: PAYMENTS.STRIPE,
    buttonTitle: t('next-step'),
    arrow: true,
  },
}

const cmsPaymentTypes = computed(() =>
  (props.options || []).map(paymentType => ({
    label: paymentSystems[paymentType.fields.key]?.label || '',
    subTitle: paymentSystems[paymentType.fields.key]?.subTitle || '',
    value: paymentType.fields.key,
  })),
)

onMounted(() => {
  paymentModel.value =
    props.payment && cmsPaymentTypes.value.some(type => type.value === props.payment)
      ? props.payment
      : cmsPaymentTypes.value[0]?.value || 'swish'
})

const formVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'xl:max-w-[575px] w-full',
      [THEMES.RDK]:
        'text-purple-act-dark [&_label]:text-purple-act-dark font-avenir max-w-[650px] flex flex-col items-center',
      [THEMES.DJURENS_RATT]: 'max-w-[650px] flex flex-col items-center pb-12 -mt-8 lg:-mt-4',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
